import { t } from "i18next";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { useContext, useEffect, useState } from "react";
import { getAthlete, getAthleteAcitivities } from "../../../../services/athlete";
import { Alert, Badge, Button, Col, Form, InputGroup, ListGroup, Row } from "react-bootstrap";
import moment from 'moment';
import { BarsChart } from "../../../charts/bars-chart";
import { Athlete } from "../../../../interfaces/athlete";
import { CoachReportsAcvtivityView } from "./reports-activity-view";
import { UserContext } from "../../../../App";

const emptyReportsData: any = {
    bpm: 0,
    speed: 0,
    duration: 0,
    calories: 0,
    datesTree: [],
    activities: []
}

const MONTH_NAMES: any = {
    '01': 'January', '02': 'February', '03': 'March', '04': 'April',
    '05': 'May', '06': 'June', '07': 'July', '08': 'August',
    '09': 'September', '10': 'October', '11': 'November', '12': 'December'
};

const avgOf = (items: any[], field: string) => {
    if (items.length === 0) {
        return 0;
    }
    const sum = items.reduce((acc: number, a: any) => acc + (Number(a[field]) || 0), 0);
    return sum / items.length;
};

const sumOf = (items: any[], field: string) => {
    return items.reduce((acc: number, a: any) => acc + (Number(a[field]) || 0), 0);
};

// Computes the full set of metrics for a list of activities (a day, month,
// year or the whole selection). Averages are per activity; totals are sums.
const aggregate = (acts: any[]) => {
    const speed = avgOf(acts, 'averageSpeedInMetersPerSecond');
    const bpm = avgOf(acts, 'averageHeartRateInBeatsPerMinute');
    return {
        bpm: bpm,
        speed: speed,
        duration: avgOf(acts, 'durationInSeconds'),
        calories: avgOf(acts, 'activeKilocalories'),
        maxHr: avgOf(acts, 'maxHeartRateInBeatsPerMinute'),
        distanceTotal: sumOf(acts, 'distanceInMeters'),
        durationTotal: sumOf(acts, 'durationInSeconds'),
        // Aerobic efficiency index: average speed per heartbeat, scaled for readability
        efficiency: bpm > 0 ? (speed / bpm) * 100 : 0,
        num_activities: acts.length
    };
};

// Builds the year/month/day aggregation tree client-side from the flat
// activities array, optionally filtered by activity type. Averages are computed
// per activity at every level (clean mean, no day-level accumulation bias).
function buildReports(activities: any[], type: string) {
    const filtered = type ? activities.filter((a: any) => a.activityType === type) : activities;

    const yearsMap: any = {};
    for (const a of filtered) {
        const y = String(a.year);
        const m = String(a.month).padStart(2, '0');
        const d = String(a.day);
        yearsMap[y] = yearsMap[y] || {};
        yearsMap[y][m] = yearsMap[y][m] || {};
        yearsMap[y][m][d] = yearsMap[y][m][d] || [];
        yearsMap[y][m][d].push(a);
    }

    const datesTree = Object.keys(yearsMap).sort().map((y: string) => {
        const monthsObj = yearsMap[y];
        const yearActs: any[] = [];
        const months = Object.keys(monthsObj).sort().map((m: string) => {
            const daysObj = monthsObj[m];
            const monthActs: any[] = [];
            const days = Object.keys(daysObj).sort((a, b) => Number(a) - Number(b)).map((d: string) => {
                const dayActs = daysObj[d];
                monthActs.push(...dayActs);
                return { day: d, ...aggregate(dayActs), activities: dayActs };
            });
            yearActs.push(...monthActs);
            return { month: m, monthName: MONTH_NAMES[m] || m, ...aggregate(monthActs), days: days };
        });
        return { year: y, ...aggregate(yearActs), months: months };
    });

    return {
        ...aggregate(filtered),
        datesTree: datesTree,
        activities: filtered
    };
}

export function CoachReportsView() {
    const { userProfile } = useContext(UserContext);

    const [loaded, setLoaded] = useState(false);
    const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
    const [reportsData, setReportsData] = useState<any>(emptyReportsData);
    const [selectedYear, setSelectedYear] = useState(0);
    const [selectedMonth, setSelectedMonth] = useState(0);
    const [selectedMonthName, setSelectedMonthName] = useState('');
    const [selectedDay, setSelectedDay] = useState(0);
    const [timelineYear, setTimelineYear] = useState('');
    const [allActivities, setAllActivities] = useState<any[]>([]);
    const [activityTypes, setActivityTypes] = useState<string[]>([]);
    const [selectedActivityType, setSelectedActivityType] = useState('');
    const [avgCategories, setAvgCategories] = useState<string[]>([]);
    const [avgDataBpm, setAvgDataBpm] = useState<number[]>([]);
    const [summaryBpm, setSummaryBpm] = useState(0);
    const [avgDataSpeed, setAvgDataSpeed] = useState<number[]>([]);
    const [summarySpeed, setSummarySpeed] = useState(0);
    const [avgDataDuration, setAvgDataDuration] = useState<number[]>([]);
    const [summaryDuration, setSummaryDuration] = useState(0);
    const [avgDataCalories, setAvgDataCalories] = useState<number[]>([]);
    const [summaryCalories, setSummaryCalories] = useState(0);
    const [avgDataDistance, setAvgDataDistance] = useState<number[]>([]);
    const [avgDataSessions, setAvgDataSessions] = useState<number[]>([]);
    const [avgDataMaxHr, setAvgDataMaxHr] = useState<number[]>([]);
    const [avgDataEfficiency, setAvgDataEfficiency] = useState<number[]>([]);
    const [summarySessions, setSummarySessions] = useState(0);
    const [summaryDistanceTotal, setSummaryDistanceTotal] = useState(0);
    const [summaryDurationTotal, setSummaryDurationTotal] = useState(0);
    const [summaryMaxHr, setSummaryMaxHr] = useState(0);
    const [summaryEfficiency, setSummaryEfficiency] = useState(0);
    const [activitiesList, setActivitiesList] = useState<any>([]);
    const [selectedActivityId, setSelectedActivityId] = useState('');
    const [showActivityDetails, setShowActivityDetails] = useState(false);

    // Athlete search states
    const [allAthletes, setAllAthletes] = useState<Athlete[]>([]);
    const [athleteSearchTerm, setAthleteSearchTerm] = useState('');
    const [athleteSearchResults, setAthleteSearchResults] = useState<Athlete[]>([]);

    // Load all athletes on mount
    useEffect(() => {
        getAthlete('', '', '', '', 'display_name', 'ASC', 1, 10000)
            .then(response => {
                setAllAthletes(response.data);
                setLoaded(true);
            })
            .catch((error: any) => {
                console.error('Error loading athletes:', error);
                setLoaded(true);
            });
    }, []);

    // Local search for athletes - filters by name, club and team
    const handleAthleteSearch = (searchValue: string) => {
        setAthleteSearchTerm(searchValue);

        if (searchValue.length < 2) {
            setAthleteSearchResults([]);
            return;
        }

        const searchLower = searchValue.toLowerCase();

        const filtered = allAthletes.filter((athlete: Athlete) => {
            // Search by athlete name
            if (athlete.name && athlete.name.toLowerCase().includes(searchLower)) {
                return true;
            }
            // Search by email
            if (athlete.email && athlete.email.toLowerCase().includes(searchLower)) {
                return true;
            }
            // Search by team name or club name in registered_in
            if (athlete.registered_in && athlete.registered_in.length > 0) {
                for (const reg of athlete.registered_in) {
                    if (reg.team_name && reg.team_name.toLowerCase().includes(searchLower)) {
                        return true;
                    }
                    if (reg.club_name && reg.club_name.toLowerCase().includes(searchLower)) {
                        return true;
                    }
                }
            }
            return false;
        });

        // Limit to 20 results and sort by name
        const results = filtered.slice(0, 20).sort((a: Athlete, b: Athlete) =>
            a.name.localeCompare(b.name)
        );

        setAthleteSearchResults(results);
    };

    // Select an athlete and load their activities
    const handleSelectAthlete = (athlete: Athlete) => {
        setSelectedAthlete(athlete);
        setAthleteSearchTerm('');
        setAthleteSearchResults([]);
        setSelectedActivityType('');

        if (!athlete.garmin_user_id || athlete.garmin_user_id.length === 0) {
            setReportsData(emptyReportsData);
            setAllActivities([]);
            setActivityTypes([]);
            return;
        }

        loadActivities(athlete.garmin_user_id);
    };

    // Fetch all kayak activities for a Garmin user (single request). Type
    // filtering is then applied client-side without hitting the slow Garmin API.
    const loadActivities = (garminUserId: string) => {
        setLoaded(false);
        const userIds: string[] = [garminUserId];
        getAthleteAcitivities(userIds)
            .then((responseAthleteActivities: any) => {
                const data = responseAthleteActivities.data;
                const activities = data.activities || [];
                setAllActivities(activities);
                setActivityTypes(data.activityTypes || []);
                setSelectedActivityType('');
                applyDefaultView(buildReports(activities, ''));
                setLoaded(true);
            })
            .catch((error: any) => {
                console.error('Error loading athlete activities:', error);
                setLoaded(true);
                alert(t("Error loading athlete activities. Please try again."));
            });
    };

    // Applies a (re)built reports object and selects the default year (current
    // year if present, otherwise the most recent), showing its monthly bars.
    const applyDefaultView = (data: any) => {
        setReportsData(data);
        setSelectedMonth(0);
        setSelectedMonthName('');
        setSelectedDay(0);
        setActivitiesList([]);
        if (data.datesTree.length > 0) {
            const years = data.datesTree.map((y: any) => String(y.year)).sort();
            const currentYear = String(new Date().getFullYear());
            const defaultYear = years.includes(currentYear) ? currentYear : years[years.length - 1];
            const dataTree: any = data.datesTree.filter((yf: any) => String(yf.year) == defaultYear)[0];
            setTimelineYear(defaultYear);
            setSelectedYear(defaultYear as any);
            applyNodeSummary(dataTree);
            applyChartNodes(dataTree.months, (m: any) => t(m.monthName));
        } else {
            setSelectedYear(0);
            setTimelineYear('');
            applyNodeSummary({});
            applyChartNodes([], (n: any) => n);
        }
    };

    const handleActivityTypeChange = (event: any) => {
        const type = event.target.value;
        setSelectedActivityType(type);
        applyDefaultView(buildReports(allActivities, type));
    };

    // Human-friendly label for a raw Garmin activity type
    const formatActivityType = (type: string) => {
        const known: any = {
            KAYAKING: t('Kayak'),
            KAYAKING_V2: t('Kayak'),
            WHITEWATER_RAFTING_KAYAKING: t('Whitewater kayak'),
            CANOEING: t('Canoe'),
            CANOEING_V2: t('Canoe'),
            STAND_UP_PADDLEBOARDING: t('Paddle surf')
        };
        if (known[type]) {
            return known[type];
        }
        return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
    };

    // Clear selected athlete and go back to search
    const handleClearAthlete = () => {
        setSelectedAthlete(null);
        setReportsData(emptyReportsData);
        setSelectedYear(0);
        setSelectedMonth(0);
        setSelectedMonthName('');
        setSelectedDay(0);
        setTimelineYear('');
        setSelectedActivityType('');
        setAllActivities([]);
        setActivityTypes([]);
        setAvgCategories([]);
        setAvgDataBpm([]);
        setAvgDataSpeed([]);
        setAvgDataDuration([]);
        setAvgDataCalories([]);
        setSummaryBpm(0);
        setSummarySpeed(0);
        setSummaryDuration(0);
        setSummaryCalories(0);
    };

    const handleSelectTreeItem = (event: any) => {
        selectTreeItem(event.currentTarget.dataset.year, event.currentTarget.dataset.month, event.currentTarget.dataset.day);
    }

    const goToYear = (year: string) => {
        if (!year) {
            return;
        }
        setTimelineYear(year);
        selectTreeItem(year, 0, 0);
    }

    const applyNodeSummary = (node: any) => {
        setSummaryBpm(node.bpm || 0);
        setSummarySpeed(node.speed || 0);
        setSummaryDuration(node.duration || 0);
        setSummaryCalories(node.calories || 0);
        setSummaryMaxHr(node.maxHr || 0);
        setSummaryEfficiency(node.efficiency || 0);
        setSummarySessions(node.num_activities || 0);
        setSummaryDistanceTotal(node.distanceTotal || 0);
        setSummaryDurationTotal(node.durationTotal || 0);
    };

    const applyChartNodes = (nodes: any[], labelFn: (n: any) => any) => {
        setAvgCategories(nodes.map(labelFn));
        setAvgDataBpm(nodes.map((n: any) => n.bpm.toFixed(2)));
        setAvgDataSpeed(nodes.map((n: any) => n.speed));
        setAvgDataDuration(nodes.map((n: any) => n.duration));
        setAvgDataCalories(nodes.map((n: any) => n.calories));
        setAvgDataMaxHr(nodes.map((n: any) => n.maxHr));
        setAvgDataDistance(nodes.map((n: any) => +((n.distanceTotal || 0) / 1000).toFixed(2)));
        setAvgDataSessions(nodes.map((n: any) => n.num_activities));
        setAvgDataEfficiency(nodes.map((n: any) => +Number(n.efficiency || 0).toFixed(2)));
    };

    // Converts a raw activity into a node so the activity-level chart shares the shape
    const activityToNode = (a: any) => ({
        activityName: a.activityName,
        bpm: a.averageHeartRateInBeatsPerMinute || 0,
        speed: a.averageSpeedInMetersPerSecond || 0,
        duration: a.durationInSeconds || 0,
        calories: a.activeKilocalories || 0,
        maxHr: a.maxHeartRateInBeatsPerMinute || 0,
        distanceTotal: a.distanceInMeters || 0,
        durationTotal: a.durationInSeconds || 0,
        num_activities: 1,
        efficiency: a.averageHeartRateInBeatsPerMinute ? (a.averageSpeedInMetersPerSecond / a.averageHeartRateInBeatsPerMinute) * 100 : 0
    });

    const selectTreeItem = (year: any, month: any, day: any) => {
        setSelectedYear(year);
        setSelectedMonth(month);
        setSelectedMonthName('');
        setSelectedDay(day);
        setActivitiesList([]);
        if (year == 0 && month == 0 && day == 0) {
            applyNodeSummary(reportsData);
            applyChartNodes(reportsData.datesTree, (y: any) => y.year);
        }
        if (year != 0 && month == 0 && day == 0) {
            const dataTree: any = reportsData.datesTree.filter((yf: any) => yf.year == year)[0];
            applyNodeSummary(dataTree);
            applyChartNodes(dataTree.months, (m: any) => t(m.monthName));
        }
        if (year != 0 && month != 0 && day == 0) {
            const dataTreeYear: any = reportsData.datesTree.filter((yf: any) => yf.year == year)[0];
            const dataTreeMonth: any = dataTreeYear.months.filter((mf: any) => mf.month == month)[0];
            setSelectedMonthName(dataTreeMonth.monthName);
            applyNodeSummary(dataTreeMonth);
            applyChartNodes(dataTreeMonth.days, (d: any) => d.day);
        }
        if (year != 0 && month != 0 && day != 0) {
            const dataTreeYear: any = reportsData.datesTree.filter((yf: any) => yf.year == year)[0];
            const dataTreeMonth: any = dataTreeYear.months.filter((mf: any) => mf.month == month)[0];
            const dataTreeDay: any = dataTreeMonth.days.filter((df: any) => df.day == day)[0];
            applyNodeSummary(dataTreeDay);
            setActivitiesList(dataTreeDay.activities);
            applyChartNodes(dataTreeDay.activities.map(activityToNode), (n: any) => n.activityName);
        }
    }

    const handleShowActivityDetails = (event: any) => {
        setSelectedActivityId(event.currentTarget.dataset.activityid);
        setShowActivityDetails(true);
    }

    const handleHideActivityDetails = (event: any) => {
        setSelectedActivityId('');
        setShowActivityDetails(false);
    }

    const availableYears: string[] = reportsData.datesTree.map((y: any) => String(y.year)).sort();
    const timelineIndex = availableYears.indexOf(timelineYear);
    const prevYear = timelineIndex > 0 ? availableYears[timelineIndex - 1] : '';
    const nextYear = (timelineIndex >= 0 && timelineIndex < availableYears.length - 1) ? availableYears[timelineIndex + 1] : '';
    const currentYearTree: any = reportsData.datesTree.filter((y: any) => String(y.year) == timelineYear)[0];

    return (
        <div className="reports-container">
            { !loaded && 
                <LoadingSpinner />
            }
            { loaded &&
                <div className="reports-content">
                    <h1>{ t("Athlete reports") }</h1>
                    <div className="filters-container mb-4">
                        {selectedAthlete ? (
                            <Alert variant="info" className="d-flex align-items-start justify-content-between mb-0">
                                <div>
                                    <div className="d-flex align-items-center mb-1">
                                        <i className="bi bi-person-fill me-2"></i>
                                        <span>{t('Athlete')}:</span>
                                        <strong className="ms-1">{selectedAthlete.name}</strong>
                                    </div>
                                    <div className="small text-muted">
                                        {selectedAthlete.email && (
                                            <span>{selectedAthlete.email}</span>
                                        )}
                                        {selectedAthlete.registered_in && selectedAthlete.registered_in.length > 0 && (
                                            <>
                                                {selectedAthlete.registered_in.map((reg, idx: number) => (
                                                    <span key={idx} className="ms-3">
                                                        <i className="bi bi-people me-1"></i>
                                                        {reg.team_name}
                                                        {reg.club_name && (
                                                            <span className="ms-1">
                                                                (<i className="bi bi-building me-1"></i>{reg.club_name})
                                                            </span>
                                                        )}
                                                    </span>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <Button variant="outline-primary" size="sm" onClick={handleClearAthlete}>
                                    <i className="bi bi-arrow-left me-1"></i>
                                    {t('Change athlete')}
                                </Button>
                            </Alert>
                        ) : (
                            <div className="athlete-search position-relative">
                                <Form.Label>{t("Athlete")}</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <i className="bi bi-search"></i>
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('Search athlete by name, team or club...')}
                                        value={athleteSearchTerm}
                                        onChange={(e) => handleAthleteSearch(e.target.value)}
                                    />
                                </InputGroup>
                                {athleteSearchResults.length > 0 && (
                                    <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                        {athleteSearchResults.map((athlete: Athlete) => (
                                            <ListGroup.Item
                                                key={athlete.id}
                                                action
                                                onClick={() => handleSelectAthlete(athlete)}
                                                className="py-2"
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="fw-medium">
                                                            <i className="bi bi-person me-2"></i>
                                                            {athlete.name}
                                                            {!athlete.garmin_user_id && (
                                                                <Badge bg="warning" className="ms-2">{t('No Garmin')}</Badge>
                                                            )}
                                                        </div>
                                                        {athlete.email && (
                                                            <small className="text-muted">{athlete.email}</small>
                                                        )}
                                                    </div>
                                                    <div className="text-end">
                                                        {athlete.registered_in && athlete.registered_in.length > 0 && (
                                                            <div>
                                                                {athlete.registered_in.map((reg, idx: number) => (
                                                                    <small key={idx} className="text-muted d-block">
                                                                        <i className="bi bi-people me-1"></i>
                                                                        {reg.team_name}
                                                                        {reg.club_name && (
                                                                            <span className="ms-1">
                                                                                <i className="bi bi-building ms-2 me-1"></i>
                                                                                {reg.club_name}
                                                                            </span>
                                                                        )}
                                                                    </small>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </div>
                        )}
                    </div>
                    <Row>
                        <Col xs={ 12 } lg={ 4 } xl={ 3 }>
                            { activityTypes.length > 0 &&
                                <div className="activity-type-filter mb-3">
                                    <Form.Label>{ t("Activity type") }</Form.Label>
                                    <Form.Select value={ selectedActivityType } onChange={ handleActivityTypeChange }>
                                        <option value="">{ t("All types") }</option>
                                        { activityTypes.map((at: string) => (
                                            <option key={ at } value={ at }>{ formatActivityType(at) }</option>
                                        ))}
                                    </Form.Select>
                                </div>
                            }
                            <div className="timeline">
                                <h2>{ 'Timeline' }</h2>
                                { reportsData.datesTree.length == 0 && 
                                    <Alert key={'info'} variant={'info'}>
                                        { t("This athlete has no activity data.") }
                                    </Alert>
                                }
                                { reportsData.datesTree.length > 0 && 
                                    <div className="timeline-items">
                                        <div className={ 'activity-all' + ((selectedYear == 0 && selectedMonth == 0 && selectedDay == 0) ? ' active' : '') } key={ 'a-all' }>
                                            <button className="btn-unformatted" data-year={ 0 } data-month={ 0 } data-day={ 0 } onClick={ handleSelectTreeItem }>
                                                { t("All activities") }
                                            </button>
                                        </div>
                                        <div className="year-nav">
                                            <button className="btn-unformatted year-arrow" disabled={ !prevYear } title={ t("Previous year") } onClick={ () => goToYear(prevYear) }>
                                                <i className="bi bi-chevron-left"></i>
                                            </button>
                                            <button className={ 'btn-unformatted year-label' + ((selectedYear == (timelineYear as any) && selectedMonth == 0 && selectedDay == 0) ? ' active' : '') } data-year={ timelineYear } data-month={ 0 } data-day={ 0 } onClick={ handleSelectTreeItem }>
                                                { timelineYear }
                                            </button>
                                            <button className="btn-unformatted year-arrow" disabled={ !nextYear } title={ t("Next year") } onClick={ () => goToYear(nextYear) }>
                                                <i className="bi bi-chevron-right"></i>
                                            </button>
                                        </div>
                                        { currentYearTree &&
                                            <div className="year-months">
                                                { currentYearTree.months.map((m: any) => {
                                                    return (<div className={ 'activity-month' + ((selectedYear == (timelineYear as any) && selectedMonth == m.month) ? ' active' : '') } key={ 'a-' + timelineYear + m.month }>
                                                        <button className="btn-unformatted" data-year={ timelineYear } data-month={ m.month } data-day={ 0 } onClick={ handleSelectTreeItem }>
                                                            { t(m.monthName) }
                                                        </button>
                                                        <div className="month-days">
                                                            { m.days.map((d: any) => {
                                                                return (<div className={ 'activity-day' + ((selectedYear == (timelineYear as any) && selectedMonth == m.month && selectedDay == d.day) ? ' active' : '') } key={ 'a-' + timelineYear + m.month + d.day }>
                                                                    <button className="btn-unformatted" data-year={ timelineYear } data-month={ m.month } data-day={ d.day } onClick={ handleSelectTreeItem }>
                                                                        { d.day }
                                                                    </button>
                                                                </div>)
                                                            })}
                                                        </div>
                                                    </div>)
                                                })}
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        </Col>    
                        <Col xs={ 12 } lg={ 8 } xl={ 9 }>
                            { reportsData.datesTree.length > 0 && 
                                <div className="report-data-content">
                                    <h2>
                                        { t("Aggregated data for") }
                                        { selectedYear == 0 && <strong>&nbsp;{ t("All activities") }</strong> }
                                        { selectedYear != 0 && selectedMonth == 0 && <strong>&nbsp;{ selectedYear }</strong> }
                                        { selectedYear != 0 && selectedMonth != 0 && selectedDay == 0 && <strong>&nbsp;{ t(selectedMonthName) + ' ' + selectedYear }</strong> }
                                        { selectedYear != 0 && selectedMonth != 0 && selectedDay != 0 && <strong>&nbsp;{ selectedDay + '/' + selectedMonth + '/' + selectedYear }</strong> }
                                    </h2>
                                    <div className="summary">
                                        <div className="totals">
                                            <div className="heartrate activity-kpi">
                                                <div className="value">
                                                    { summaryBpm.toFixed(2) } <span className="units">{ t("bpm") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Heart Rate Avg") }
                                                </div>
                                            </div>
                                            <div className="cadence activity-kpi">
                                                <div className="value">
                                                    { summarySpeed.toFixed(2) } <span className="units">{ t("m/s") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Speed Avg") }
                                                </div>
                                            </div>
                                            <div className="duration activity-kpi">
                                                <div className="value">
                                                    {  moment.utc(summaryDuration*1000).format('HH:mm:ss') }
                                                </div>
                                                <div className="label">
                                                    { t("Duration Avg") }
                                                </div>
                                            </div>
                                            <div className="calories activity-kpi">
                                                <div className="value">
                                                    { summaryCalories.toFixed(2) } <span className="units">{ t("kcal") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Active Calories Avg") }
                                                </div>
                                            </div>
                                            <div className="sessions activity-kpi">
                                                <div className="value">
                                                    { summarySessions }
                                                </div>
                                                <div className="label">
                                                    { t("Sessions") }
                                                </div>
                                            </div>
                                            <div className="distance activity-kpi">
                                                <div className="value">
                                                    { (summaryDistanceTotal / 1000).toFixed(2) } <span className="units">{ t("km") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Total distance") }
                                                </div>
                                            </div>
                                            <div className="total-time activity-kpi">
                                                <div className="value">
                                                    { moment.utc(summaryDurationTotal * 1000).format('HH:mm:ss') }
                                                </div>
                                                <div className="label">
                                                    { t("Total time") }
                                                </div>
                                            </div>
                                        </div>
                                        { activitiesList.length > 0 && 
                                            <div className="activities-list">
                                                <h4>{ t("Activities list") }</h4>
                                                <ol className="list">
                                                    {
                                                        activitiesList.map((a: any) => {
                                                            const activityDate = new Date((a.startTimeInSeconds + a.startTimeOffsetInSeconds)*1000);
                                                            const activityDateFormatted = moment(activityDate).format('DD/MM/Y H:mm')
                                                            return <li className="activity-item" key={ 'activity-item-' + a.activityId }>
                                                                    <button className="btn-unformatted" data-activityid={ a.activityId }
                                                                        onClick={ handleShowActivityDetails }>{ activityDateFormatted + ' - ' + a.activityName }</button>
                                                                </li>
                                                        })
                                                    }
                                                </ol>
                                            </div>
                                        }
                                    </div>
                                    { avgCategories.length > 0 && avgDataBpm.length > 0 && 
                                        <BarsChart title={ t("Heart Rate Avg") } 
                                            category={ avgCategories }
                                            data={ avgDataBpm }
                                            units={ t("bpm") }
                                            color={ '#34B689' }
                                            yAxisType="value" />
                                    }
                                    { avgCategories.length > 0 && avgDataSpeed.length > 0 && 
                                        <BarsChart title={ t("Speed Avg") } 
                                            category={ avgCategories }
                                            data={ avgDataSpeed }
                                            units={ t("m/s") }
                                            color={ '#FABF48' }
                                            yAxisType="value" />
                                    }
                                    { avgCategories.length > 0 && avgDataDuration.length > 0 && 
                                        <BarsChart title={ t("Duration Avg") } 
                                            category={ avgCategories }
                                            data={ avgDataDuration }
                                            units={ '' }
                                            color={ '#F92C84' }
                                            yAxisType="time" />
                                    }
                                    { avgCategories.length > 0 && avgDataCalories.length > 0 &&
                                        <BarsChart title={ t("Active Calories") }
                                            category={ avgCategories }
                                            data={ avgDataCalories }
                                            units={ t("kcal") }
                                            color={ '#009FE3' }
                                            yAxisType="value" />
                                    }
                                    { avgCategories.length > 0 && avgDataDistance.length > 0 &&
                                        <BarsChart title={ t("Total distance") }
                                            category={ avgCategories }
                                            data={ avgDataDistance }
                                            units={ t("km") }
                                            color={ '#6f42c1' }
                                            yAxisType="value" />
                                    }
                                    { avgCategories.length > 0 && avgDataSessions.length > 0 &&
                                        <BarsChart title={ t("Sessions") }
                                            category={ avgCategories }
                                            data={ avgDataSessions }
                                            units={ '' }
                                            color={ '#20c997' }
                                            yAxisType="value" />
                                    }
                                    { avgCategories.length > 0 && avgDataMaxHr.length > 0 &&
                                        <BarsChart title={ t("Max Heart Rate") }
                                            category={ avgCategories }
                                            data={ avgDataMaxHr }
                                            units={ t("bpm") }
                                            color={ '#dc3545' }
                                            yAxisType="value" />
                                    }
                                    { avgCategories.length > 0 && avgDataEfficiency.length > 0 &&
                                        <BarsChart title={ t("Aerobic efficiency") }
                                            subtitle={ t("Aerobic efficiency formula") }
                                            category={ avgCategories }
                                            data={ avgDataEfficiency }
                                            units={ '' }
                                            color={ '#fd7e14' }
                                            yAxisType="value" />
                                    }
                                </div>
                            }
                        </Col>                        
                    </Row>
                    { showActivityDetails && selectedActivityId != '' && selectedAthlete &&
                        <CoachReportsAcvtivityView userId={ selectedAthlete.garmin_user_id } activityId={ selectedActivityId } onClose={ handleHideActivityDetails } />
                    }
                </div>
            }
        </div>
    );
}